import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from '@dev-go4ellis/go4-design-system';
import WhatFeeCovers from '../WhatFeeCovers/WhatFeeCovers';
import ComparisonTable from '../ComparisonTable/ComparisonTable';
import './CostEstimator.scss';

const FEE_PERCENTAGE = 0.175;

const ZIP_RANGES = [
  [6, 6, 'NY'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
  [50, 54, 'VT'], [55, 55, 'MA'], [56, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
  [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
  [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'],
  [300, 319, 'GA'], [320, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'],
  [398, 399, 'GA'], [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
  [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'], [580, 588, 'ND'],
  [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
  [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'],
  [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'],
  [889, 898, 'NV'], [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK'],
];

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'Washington, D.C.', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
  ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const STATE_AVG = {
  AL: 44, AK: 58, AZ: 49, AR: 42, CA: 62, CO: 52, CT: 55, DE: 48, DC: 60, FL: 47, GA: 50.39, HI: 57,
  ID: 45, IL: 53, IN: 46, IA: 45, KS: 44, KY: 43, LA: 44, ME: 46, MD: 54, MA: 58, MI: 48, MN: 51,
  MS: 41, MO: 45, MT: 46, NE: 45, NV: 50, NH: 52, NJ: 56, NM: 45, NY: 60, NC: 48, ND: 47, OH: 47,
  OK: 43, OR: 53, PA: 51, RI: 52, SC: 46, SD: 44, TN: 45, TX: 50, UT: 47, VT: 48, VA: 52, WA: 56,
  WV: 43, WI: 49, WY: 46,
};

const stateFromZip = (zip) => {
  if (!zip || zip.length < 5) return null;
  const p = parseInt(zip.slice(0, 3), 10);
  if (Number.isNaN(p)) return null;
  const found = ZIP_RANGES.find(([lo, hi]) => p >= lo && p <= hi);
  return found ? found[2] : null;
};

const formatMoney = (n) => {
  const num = Number(n) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

class CostEstimator extends Component {
  state = { zip: '', hours: '', trainers: '', rate: '' };

  onZipChange = (e) => {
    const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
    this.setState({ zip });
  };

  onNumberChange = (field) => (e) => {
    const raw = e.target.value;
    if (raw === '') { this.setState({ [field]: '' }); return; }
    const value = Number(raw);
    if (!Number.isNaN(value) && value >= 0) this.setState({ [field]: value });
  };

  avg = () => {
    const st = stateFromZip(this.state.zip);
    if (!st || STATE_AVG[st] === undefined) return null;
    return { state: st, name: STATE_NAMES[st] || st, rate: STATE_AVG[st] };
  };

  useAvg = () => {
    const a = this.avg();
    if (a) this.setState({ rate: a.rate });
  };

  hasInput = () => this.state.hours !== '' && this.state.trainers !== '' && this.state.rate !== '';

  calculate = () => {
    const hours = Number(this.state.hours) || 0;
    const trainers = Number(this.state.trainers) || 0;
    const rate = Number(this.state.rate) || 0;
    const basePay = hours * trainers * rate;
    const fee = basePay * FEE_PERCENTAGE;
    return { basePay, fee, total: basePay + fee };
  };

  render() {
    const { zip, hours, trainers, rate } = this.state;
    const { basePay, fee, total } = this.calculate();
    const filled = this.hasInput();
    const a = this.avg();
    const avgLabel = a ? `$${a.rate.toFixed(2)}/hr` : '—';
    const helper = a
      ? `Athletic trainers in ${a.name} have worked jobs at an average of $${a.rate.toFixed(2)} per hour. Use it as a starting point, then adjust.`
      : "Enter your ZIP and we'll show the local average trainers have actually been paid.";

    return (
      <section id={this.props.id} className="pl-est">
        <div className="pl-est__inner">
          <div className="pl-est__head">
            <div className="pl-est__eyebrow">Cost estimator</div>
            <h2 className="pl-est__title">Price your event now</h2>
            <p className="pl-est__subtitle">Enter your ZIP code and we'll show what athletic trainers in your area have actually been paid, then build your estimate from there. Go4 does not set rates — you choose the rate.</p>
          </div>

          <div className="pl-est__grid">
            <Card padding="lg" flat className="pl-est__card">
              <div className="pl-est__row2">
                <div className="pl-est__field">
                  <label className="pl-est__label">ZIP Code</label>
                  <input type="text" inputMode="numeric" maxLength={5} placeholder="e.g. 30303" className="pl-est__input" value={String(zip)} onChange={this.onZipChange} />
                </div>
                <div className="pl-est__field">
                  <label className="pl-est__label">Avg. rate in your area</label>
                  <div className="pl-est__avg">
                    <span className="pl-est__avg-value">{avgLabel}</span>
                    {a && <button type="button" className="pl-est__avg-use" onClick={this.useAvg}>Use it</button>}
                  </div>
                </div>
              </div>

              <p className="pl-est__helper">{helper}</p>
              <div className="pl-est__divider" />

              <div className="pl-est__row3">
                <div className="pl-est__field">
                  <label className="pl-est__label">Hours</label>
                  <input type="text" inputMode="numeric" placeholder="0" className="pl-est__input" value={String(hours)} onChange={this.onNumberChange('hours')} />
                </div>
                <div className="pl-est__field">
                  <label className="pl-est__label">Trainers</label>
                  <input type="text" inputMode="numeric" placeholder="0" className="pl-est__input" value={String(trainers)} onChange={this.onNumberChange('trainers')} />
                </div>
                <div className="pl-est__field">
                  <label className="pl-est__label">Rate / hr</label>
                  <div className="pl-est__rate">
                    <span className="pl-est__currency">$</span>
                    <input type="text" inputMode="decimal" placeholder="0.00" className="pl-est__input pl-est__input--rate" value={String(rate)} onChange={this.onNumberChange('rate')} />
                  </div>
                </div>
              </div>

              <div className="pl-est__results">
                <div className="pl-est__result">
                  <span className="pl-est__result-label">Trainer pay</span>
                  <span className="pl-est__result-value">{filled ? formatMoney(basePay) : '—'}</span>
                </div>
                <div className="pl-est__result pl-est__result--fee">
                  <span className="pl-est__result-label">Service fee (17.5%)</span>
                  <span className="pl-est__result-value">{filled ? formatMoney(fee) : '—'}</span>
                </div>
                <div className="pl-est__result pl-est__result--total">
                  <span className="pl-est__total-label">Estimated total</span>
                  <span className="pl-est__total-value">{filled ? formatMoney(total) : '—'}</span>
                </div>
              </div>

              <div className="pl-est__cta-wrap">
                <Button variant="primary" size="lg" onClick={this.props.onPostJobClick}>Post this job free →</Button>
              </div>
              <p className="pl-est__disclaimer">Posting costs nothing. You're only charged after a trainer works the shift.</p>
            </Card>

            <div className="pl-est__side">
              <WhatFeeCovers />
              <ComparisonTable />
            </div>
          </div>
        </div>
      </section>
    );
  }
}

CostEstimator.propTypes = {
  id: PropTypes.string,
  onPostJobClick: PropTypes.func.isRequired,
};

CostEstimator.defaultProps = {
  id: undefined,
};

export default CostEstimator;
